import React, { useEffect, useRef, useCallback, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import { ConfirmVariant } from '../types';

interface VariantStyle {
    icon: string;
    button: string;
}

interface VariantStyles {
    danger: VariantStyle;
    warning: VariantStyle;
    info: VariantStyle;
    default: VariantStyle;
    error: VariantStyle;
}

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmVariant;
}

/**
 * Reusable confirmation dialog component to replace native confirm()
 */
export default function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    variant = "danger"
}: ConfirmDialogProps): React.ReactElement | null {
    const { t } = useTranslation('common');
    const dialogRef = useRef<HTMLDivElement>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Auto-focus the cancel button when modal opens (safer default)
    useEffect(() => {
        if (isOpen && cancelButtonRef.current) {
            cancelButtonRef.current.focus();
        }
    }, [isOpen]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (e: globalThis.KeyboardEvent): void => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Focus trap - keep Tab within the modal
    const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>): void => {
        if (e.key !== 'Tab' || !dialogRef.current) return;

        const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Shift+Tab from first element -> go to last
        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
        }
        // Tab from last element -> go to first
        else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
        }
    }, []);

    if (!isOpen) return null;

    const variantStyles: VariantStyles = {
        danger: {
            icon: "text-red-600 bg-red-100",
            button: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
        },
        warning: {
            icon: "text-yellow-600 bg-yellow-100",
            button: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
        },
        info: {
            icon: "text-blue-600 bg-blue-100",
            button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        },
        default: {
            icon: "text-gray-600 bg-gray-100",
            button: "bg-accent-600 hover:bg-accent-700 focus:ring-accent-500"
        },
        error: {
            icon: "text-red-600 bg-red-100",
            button: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
        }
    };

    const styles = variantStyles[variant] || variantStyles.danger;

    const dialogId = 'confirm-dialog';
    const titleId = `${dialogId}-title`;
    const descriptionId = `${dialogId}-description`;

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            ref={dialogRef}
            onKeyDown={handleKeyDown}
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Dialog */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all dark:bg-dark-card">
                    {/* Close button */}
                    <button
                        ref={closeButtonRef}
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        aria-label={t('actions.close')}
                    >
                        <X size={20} aria-hidden="true" />
                    </button>

                    <div className="p-6">
                        {/* Icon and Title */}
                        <div className="flex items-start space-x-4">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.icon}`}>
                                <AlertTriangle size={20} aria-hidden="true" />
                            </div>
                            <div className="flex-1">
                                <h3
                                    id={titleId}
                                    className="text-lg font-semibold text-gray-900 dark:text-dark-text"
                                >
                                    {title}
                                </h3>
                                <p
                                    id={descriptionId}
                                    className="mt-2 text-sm text-gray-600 dark:text-dark-muted whitespace-pre-line"
                                >
                                    {message}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                ref={cancelButtonRef}
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:bg-dark-hover dark:text-dark-text dark:hover:bg-dark-border"
                            >
                                {cancelText || t('confirm.cancelText')}
                            </button>
                            <button
                                ref={confirmButtonRef}
                                onClick={() => {
                                    onConfirm();
                                    onClose();
                                }}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 ${styles.button}`}
                            >
                                {confirmText || t('confirm.confirmText')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
